/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {Component} from 'react';
import {connect} from 'react-redux';
import DiscoverAnalytics from '../../../utils/DiscoverAnalytics';
import {DiscoverHomeRow, DiscoverUtils} from 'discover-hub';
import LearnUtilsHome from '../../containers/Learn/LearnUtils';
import './DiscoverHome.scss';
/* eslint-enable @typescript-eslint/no-unused-vars */

declare let adobeIMS: AdobeIMS;

const analytics = new DiscoverAnalytics();
let shouldHide = false;
let currentTab = 'featured';

type Props = {
	locale: string;
	env: string;
	productEntitlements: Array<string>;
	customerSegment: string;
	location: {
		pathname: any;
	};
	history: Array<Object>;
	intl: Object;
};

type State = {
	learnDataError: boolean;
};

type LearnSignal = {
	value: {
		app: any;
		level: any;
		field: any;
		playlistId: any;
		tutorialId: any;
	};
	type: string;
};

type DiscoverSignal = {
	value: {
		articleSlug: any;
		videoSlug: any;
		videoId: any;
	};
	type: string;
};

export class DiscoverHome extends Component<Props, State> {
	/**
	 * Navigates to the given learn content given parameters
	 * from the child.
	 * NOTE: This is different from the onLearnPathChange seen in
	 * other files. This primarily ONLY deals with Tutorial pages.
	 *
	 * @param {Array<LearnSignal>} learnSignals
	 */
	onLearnPathChange(learnSignals: Array<LearnSignal>) {
		// TODO: we need to consolidate all of these methods
		// that are the same ... somehow, somewhere

		const newPath = this.generateLearnPath(learnSignals);

		this.props.history.push(`${newPath}`);
	}

	/**
	 * Returns the string path generated from learn signals.
	 * TODO: This has common logic with onLearnPathChange, refactor this
	 *
	 * @param {Array<LearnSignal>} learnSignals
	 * @returns {string} - string version of the path
	 */
	generateLearnPath(learnSignals: Array<LearnSignal>) {
		let newPath = this.props.location.pathname;
		let isTutorial = false;

		learnSignals.reduce((params, signal) => {
			const signalValue = signal.value;

			if (signal.type === 'tutorial') {
				isTutorial = true;
				// NOTE: THIS LOGIC IS DIFFERENT FROM LEARNDISCOVER
				// This tutorial ensures that learn is the first and foremost
				// path variable.
				// If it is not then history gets an error when trying to push
				const extraPath = `${signalValue.app}/${signalValue.level}/${signalValue.field}/${signalValue.playlistId}/${signalValue.tutorialId}`;
				newPath = `/cc/discover/learn/${extraPath}`;
			}
			else if (signal.type === 'app') {
				let extraPath = `${signalValue.app}`;
				if (signalValue.level) {
					const queryParams = DiscoverUtils.getSearchPath({
						experienceLevel: signalValue.level,
					});

					extraPath = `${extraPath}/${queryParams}`;
				}
				const appPath = signalValue.app;
				if (appPath) {
					newPath = `/cc/discover/learn/app/${extraPath}`;
				}
				else {
					newPath = '/cc/discover/learn';
					// a fall back in case for some reason app is not provided
				}
			}

			return params;
		}, {});

		return newPath;
	}

	/**
	 * Returns the string path generated from discover signals.
	 *
	 * @param {DiscoverSignal} discoverSignal
	 * @returns {string} - string version of the path
	 */
	generateDiscoverPath(discoverSignal: DiscoverSignal) {
		const slash = this.props.location.pathname.includes('cc/') ? '' : '/';
		let newPath = `${this.props.location.pathname}${slash}discover`;

		const category = discoverSignal.value;

		if (category) {
			newPath = `${newPath}/${category}`;
		}

		return newPath;
	}

	/**
	 * Callback function to be passed into discover-hub
	 * component to allow route changes.
	 *
	 * NOTE: THIS METHOD IS A CONDENSED VERSION FROM DISCOVER.JS
	 *
	 * @param {Array<DiscoverSignal>} arg1
	 * @param {bool} isArticle
	 */
	handleDiscoverPathChange(arg1: Array<DiscoverSignal>) {
		// This is an array, contain the signals
		// [{type: "topic", value: "photography"},
		// {type: "pageIndex", value: 1},
		// {type: "discoverCategory", value: "inspiration"}]
		const signals = arg1;
		let newPath = this.props.location.pathname; // ex. /cc b/c we should be on Home

		let isArticle = false; // TODO: Maybe there's another better way to do this
		let isVideo = false;

		const newSearchParams = signals.reduce((params, signal) => {
			const beforeDiscover = newPath.split('/discover')[0];
			const slash = beforeDiscover.includes('cc/') ? '' : '/';

			if (signal.type === 'article') {
				// push this path with go to a different route
				const extraPath = `${signal.value.articleSlug}`;
				newPath = `${beforeDiscover}${slash}discover/article/${extraPath}`; // NOTE: This drops all query params

				isArticle = true;
			}
			else if (signal.type === 'video') {
				const extraPath = `${signal.value.videoSlug}/${signal.value.videoId}`;
				newPath = `${beforeDiscover}${slash}discover/video/${extraPath}`;

				isVideo = true;
			}
			else if (signal.type === 'discoverCategory') {
				const fullPath = this.generateDiscoverPath(signal);
				// 'See More' link click
				newPath = fullPath;
			}
			return params;
		}, {});

		if (isArticle || isVideo) {
			this.props.history.push({
				pathname: `${newPath}`,
				state: {
					currentTab,
					// topic: this.state.topic
				},
				onDiscoverPathChange: this.handleDiscoverPathChange.bind(this),
			});
		}
		else {
			// this should only happen w/ the vanilla see more link
			this.props.history.push(`${newPath}`);
		}
	}

	/**
	 * Hides the component in case of errors
	 * @param {boolean} value
	 */
	toggleComponentHide(value: boolean) {
		shouldHide = value;
	}

	/**
	 * @param {string} tab - current tab from DiscoverHomeRow component
	 */
	setCurrentTab(tab: string) {
		currentTab = tab;
	}

	/**
	 * Returns IMS Data required for making authorized server requests for discover on home
	 * @returns {object}
	 */
	getIMSData() {
		return {
			bearerToken: adobeIMS.getAccessToken(),
			token: adobeIMS.getAccessToken(), // for learn
			userId: window.cchome.util.getUserId(),
		};
	}

	render() {
		let discoverHome;
		if (window.cchome.util.isIE() || !window.cchome.util.hasFeature('discover_on_home')) {
			discoverHome = null;
		}
		else {
			discoverHome = !shouldHide && (
				<div className='discover-home-component'>
					<DiscoverHomeRow
						env={this.props.env}
						imsData={this.getIMSData()}
						locale={this.props.locale}
						featureFlags={window.cchome.util.getAllFeatures()}
						intl={this.props.intl}
						logger={window.cchome.util.logger}
						location={this.props.location}
						postAnalytics={data => analytics.postCustomData(data)}
						onDiscoverPathChange={data => this.handleDiscoverPathChange(data)}
						onLearnPathChange={data => this.onLearnPathChange(data)}
						generateDiscoverPath={data => this.generateDiscoverPath(data)}
						toggleComponentHide={this.toggleComponentHide}
						setCurrentTab={tab => this.setCurrentTab(tab)}
					/>
				</div>
			);
		}

		return discoverHome;
	}
}

function mapStateToProps(state) {
	return {
		productEntitlements: state.profile.productEntitlements,
	};
}

export default connect(mapStateToProps, {})(DiscoverHome);
